"use client";

import { motion } from "framer-motion";
import { SectionHeading } from "@/components/ui/section-heading";
import { LinkButton, GetEarlyAccessButton } from "@/components/ui/buttons";
import { LogoMark } from "@/components/ui/logo";
import { ease } from "@/lib/motion";
import { cn } from "@/lib/utils";

/**
 * Two plans, on the reference layout the team supplied on 2026-08-25: a
 * self-serve gateway plan and a custom enterprise one, with the three
 * "pay as you go / no hidden costs / no call limit" markers under them.
 * They replaced the three-tier table (Pay as you go / Pro / Enterprise),
 * which quoted per-minute rate limits and a monthly fee that nothing on the
 * platform enforces or bills. The first card's wording ("LLM Gateway",
 * "Pay-as-you-Go", the top-up note) is the team's, verbatim, from
 * 2026-08-26; the same feedback retired "no subscription" everywhere in
 * favour of "pay as you go", and the platform-fee percentage left the card
 * with it.
 *
 * Alignment: the two cards are a CSS subgrid, so the icon, the name and
 * price, the buttons, the divider and the feature list each sit on one
 * shared row across both cards. The first card has a second button and the
 * second does not; without the subgrid its divider sat a line lower than its
 * neighbour's, which was the fault the team pointed at against the reference.
 * The free-credit promotion moved out of the card and into the paragraph
 * above on 2026-08-26 (it was the one row with content on one side only, and
 * the empty track it left made the cards look loose).
 *
 * Every figure is in Pakistani rupees, matching the ledger the platform
 * actually bills against; `Rs` rather than the `₨` glyph because that is what
 * the dashboard's formatter emits. The "5.5% platform fee" the reference
 * printed on the first card was never backed by an endpoint or a price list
 * here, and was replaced by the team's "Pay-as-you-Go" line on 2026-08-26;
 * the paragraph above still promises a transparent platform fee without
 * naming a rate.
 *
 * A CTA with `href: null` opens the early-access modal instead of
 * navigating. "Get your API key" has been one of those since 2026-08-30: it
 * went to the dashboard's keys page, which signed out is a bounce to the
 * login page, and there is no sign-up to complete while the product is gated
 * behind early access.
 */
const PLANS = [
  {
    name: "LLM Gateway",
    price: "Pay-as-you-Go",
    note: "Top up your account and pay per token usage as per models per-million-token rate.",
    primary: { label: "Get your API key", href: null as string | null },
    secondary: { label: "Browse models", href: "#models" },
    lead: null,
    features: [
      "Access to every P/9-hosted model with one unified, OpenAI-compatible API",
      "Compare models on published pedigree, latency and price",
      "Multiple API keys for different projects, each with its own scope and cap",
      "Cost and performance monitoring, per key and per model",
      "Chat support (48h, working days)",
      "Unlimited seats",
    ],
  },
  {
    name: "Enterprise Gateway Platform",
    price: "Custom price",
    note: "Quoted per organisation, for dedicated capacity and enterprise terms.",
    /**
     * Dedicated capacity is quoted, not self-served: the price depends on
     * which models are pinned and how much of a cluster they hold, so the
     * honest CTA is a conversation. It points at /dedicated, a capacity
     * enquiry, not the /engineers staffing form.
     */
    primary: { label: "Talk to sales", href: "/dedicated#talk" as string | null },
    secondary: null,
    lead: "All the previous features +",
    features: [
      "High rate limits and bulk discounts",
      "Private deployments and specific compliance needs",
      "Custom latency and cost optimisation",
      "Dedicated support, SLA and professional services",
      "Custom billing and invoicing structures",
    ],
  },
];

const MARKERS = [
  { label: "Pay as you go", glyph: "meter" },
  { label: "No hidden costs", glyph: "coin" },
  { label: "No API call limit", glyph: "rocket" },
] as const;

function MarkerGlyph({ kind }: { kind: (typeof MARKERS)[number]["glyph"] }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  if (kind === "meter") {
    /* a gauge: the meter runs only while you use it */
    return (
      <svg {...common}>
        <path d="M4 16a8 8 0 0 1 16 0" />
        <path d="M12 16l4-5" />
        <path d="M4 16h2M18 16h2M12 8v2" />
      </svg>
    );
  }
  if (kind === "coin") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="8" />
        <path d="M12 8v8M9.5 10.5c0-1 1-1.5 2.5-1.5s2.5.5 2.5 1.5-1 1.5-2.5 1.5-2.5.5-2.5 1.5 1 1.5 2.5 1.5 2.5-.5 2.5-1.5" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M5 19l4-4M14 4c3 0 6 3 6 6l-6 6-6-6z" />
      <path d="M8 10l-3 1 2 2M14 16l-1 3-2-2" />
    </svg>
  );
}

function Tick() {
  return (
    <svg
      className="mt-[0.2rem] shrink-0 text-accent"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

/**
 * The five shared rows, top to bottom. Each card is a subgrid over the same
 * five tracks of the parent, so a taller row in one card pushes the same row
 * in the other. Below `md` the cards stack and the rows collapse to flow.
 */
const ROWS = "md:grid-rows-[auto_auto_auto_auto_1fr]";

/**
 * `tight` is for `/pricing`, where this band is the first thing under the
 * headline and the page has already said the word Pricing, named the free
 * credit and promised rupees (2026-09-26, on instruction that the tiers come
 * at the top). It drops the section heading and its lead, which would
 * otherwise repeat the hero, and the padding that pushed the cards below the
 * fold. The home page renders it unchanged.
 */
export function Pricing({ tight = false }: { tight?: boolean } = {}) {
  return (
    <section id="pricing" className="relative scroll-mt-24">
      <div
        className={
          tight
            ? "mx-auto max-w-[90rem] px-5 pb-16 pt-2 sm:px-8 sm:pb-24 sm:pt-4"
            : "mx-auto max-w-[90rem] px-5 py-16 sm:px-8 sm:py-32"
        }
      >
        {!tight && <SectionHeading eyebrow="Pricing" title={<>Only pay for what you need.</>} />}
        {!tight && (
        <p className="mx-auto mt-5 max-w-xl text-center font-sans text-sm leading-relaxed text-muted">
          Just pay for the API calls you make, with a transparent platform fee.{" "}
          <strong className="font-medium text-foreground">
            New organisations start with Rs 1,000 of free credits.
          </strong>{" "}
          Every price is in PKR: quoted, billed and settled in the same currency, with no
          exchange-rate surprise at the end of the month.
        </p>
        )}

        <div className={cn("mx-auto grid max-w-5xl gap-5 md:grid-cols-2", tight ? "mt-0" : "mt-16", ROWS)}>
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease, delay: i * 0.1 }}
              className={cn(
                "relative flex flex-col rounded-lg border p-7 sm:p-8 md:grid md:grid-rows-subgrid md:row-span-5",
                i === 0 ? "border-foreground/30 bg-card shadow-xl" : "border-border bg-card/60",
              )}
            >
              {/* 1: mark */}
              <div className="grid h-14 w-14 place-items-center rounded-lg border border-border bg-background">
                <LogoMark className="h-7 w-7 text-foreground" />
              </div>

              {/* 2: name, price, note */}
              <div className="mt-6">
                <h3 className="font-serif text-3xl font-light leading-tight">{plan.name}</h3>
                <p className="mt-2 font-serif text-xl font-light text-muted">{plan.price}</p>
                <p className="mt-2 max-w-sm font-sans text-xs leading-relaxed text-muted-2">{plan.note}</p>
              </div>

              {/* 3: buttons */}
              <div className="mt-7 flex flex-wrap gap-3">
                {plan.primary.href === null ? (
                  <GetEarlyAccessButton>{plan.primary.label}</GetEarlyAccessButton>
                ) : (
                  <LinkButton href={plan.primary.href}>{plan.primary.label}</LinkButton>
                )}
                {plan.secondary && (
                  <LinkButton href={plan.secondary.href} variant="secondary" withArrow={false}>
                    {plan.secondary.label}
                  </LinkButton>
                )}
              </div>

              {/* 4: divider and lead */}
              <div className="mt-7 border-t border-border pt-5">
                {plan.lead ? (
                  <p className="font-sans text-[0.7rem] uppercase tracking-[0.16em] text-muted">{plan.lead}</p>
                ) : (
                  <p className="font-sans text-[0.7rem] uppercase tracking-[0.16em] text-muted">Included</p>
                )}
              </div>

              {/* 5: features */}
              <ul className="mt-3.5 flex flex-col gap-3">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 font-sans text-[0.95rem] leading-snug text-foreground/85">
                    <Tick />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.ul
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.7, ease, delay: 0.2 }}
          className="mx-auto mt-12 flex max-w-5xl flex-wrap items-center justify-center gap-x-10 gap-y-4"
        >
          {MARKERS.map((m) => (
            <li key={m.label} className="flex items-center gap-2.5 text-muted">
              <MarkerGlyph kind={m.glyph} />
              <span className="font-sans text-[0.72rem] uppercase tracking-[0.18em]">{m.label}</span>
            </li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}
